import '.fe{list-style:none;}.ssr-plugin'
import '.ff{padding-left:0px;}.ssr-plugin'
import '.fg{counter-reset:step-counter;}.ssr-plugin'
import '.a{display:flex;}.ssr-plugin'
import '.b{flex-direction:column;}.ssr-plugin'
import '.z{gap:2rem;}.ssr-plugin'
import '.fh{display:block;}.ssr-plugin'
import '.fi{text-decoration:none;}.ssr-plugin'
import '.fj{color:inherit;}.ssr-plugin'
import { InfoAlert } from '@app/components/core/alert/InfoAlert'
import { Text } from '@app/components/core/typography/Text'
import { Title } from '@app/components/core/typography/Title'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { Style, styled } from 'inlines'
import { match } from 'ts-pattern'
import { CardWrapper } from '../card/CardWrapper'
import { Icon } from '@app/components/core/icon/Icon'
import {
  faFile,
  faFilePdf,
  faArrowUpRightFromSquare,
} from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import useDateFormat from '@app/util/useDateFormat'
import useMediaQuery from '@app/util/useMediaQuery'
import { lazy, Suspense } from 'react'
import { PageContentBlock } from '@schema/pageContentBlock'
import { ColumnsSection, ColumnsSectionProps } from './ColumnsSection'
import { Tag } from '@app/components/core/tag/Tag'
import { SafeHtml } from '@app/components/core/safeHtml/SafeHtml'
import { ContentCollapsible } from '@app/components/blocks/contentCollapsible/ContentCollapsible'
import { MarkerType } from '@app/components/core/map/markerConfig'

const SingleMarkerMap = lazy(() =>
  import('@app/components/core/map/SingleMarkerMap').then((module) => ({
    default: module.SingleMarkerMap,
  })),
)

export type TextSectionType = {
  type: 'text'
  title: string
  body: string
  truncationThreshold: number
}
export type DataSectionType = {
  type: 'data'
  title: string
  listItems: DataSectionItem[]
}
export type StepSectionType = { type: 'steps'; listItems: StepSectionItem[] }
export type DocumentSectionType = {
  type: 'documents'
  listItems: DocumentSectionItem[]
}
export type LocationSectionType = {
  type: 'location'
  title: string
  subtitle: string
  lat: number
  long: number
  marker: MarkerType
}
export type ColumnsSectionType = ColumnsSectionProps & {
  type: 'column'
}

export type ContentSectionType =
  | PageContentBlock
  | LocationSectionType
  | TextSectionType
  | DataSectionType
  | StepSectionType
  | DocumentSectionType
  | ColumnsSectionType

export type DataSectionItemStyling = 'text' | 'multi-line-text' | 'tag'
export type DataSectionItem = {
  label: string
  content: string
  styling?: DataSectionItemStyling
}
export type StepSectionItem = { content: string; alert?: string }
export type DocumentSectionItem = {
  label: string
  file: {
    name: string
    mimeType: string
    createdAt: number
    src: string
  }
}

export type ContentSectionsProps = {
  sections: ContentSectionType[]
}

export const ContentSections = ({ sections }: ContentSectionsProps) => {
  return sections.map((section, index) => (
    <ContentSection key={index} section={section} />
  ))
}

export type ContentSectionProps = {
  section: ContentSectionType
}

export const ContentSection = ({ section }: ContentSectionProps) => {
  return match(section)
    .with({ type: 'text' }, (s) => <TextSection {...s} />)
    .with({ type: 'data' }, (s) => <DataSection {...s} />)
    .with({ type: 'steps' }, (s) => <StepsSection {...s} />)
    .with({ type: 'documents' }, (s) => <DocumentsSection {...s} />)
    .with({ type: 'column' }, (s) => <ColumnsSection {...s} />)
    .with({ type: 'location' }, (s: LocationSectionType) => (
      <LocationSection {...s} />
    ))
    .exhaustive()
}

export const Header = ({ title }: { title: string }) => {
  return <Title.H4>{title}</Title.H4>
}

const TextSection = ({ title, body, truncationThreshold }: TextSectionType) => {
  return (
    <Column gap={0}>
      <Header title={title} />
      <ContentCollapsible truncateAfter={truncationThreshold}>
        <SafeHtml html={body} />
      </ContentCollapsible>
    </Column>
  )
}

const DataSection = ({ title, listItems }: DataSectionType) => {
  const isMobile = useMediaQuery()
  const gap = isMobile ? '1rem' : '1.5rem'
  return (
    <Column gap={gap} alignItems="stretch">
      <Header title={title} />
      {isMobile ? (
        <DataRows data={listItems} />
      ) : (
        <DataTable data={listItems} />
      )}
    </Column>
  )
}

const DataSectionContent = ({
  content,
  styling = 'text',
}: {
  content: string
  styling?: DataSectionItemStyling
}) => {
  switch (styling) {
    case 'multi-line-text':
      return (
        <Column gap="0.25rem">
          {content.split(',')?.map((line, index) => (
            <Text.Medium key={index}>{line.trim()}</Text.Medium>
          ))}
        </Column>
      )
    case 'tag':
      return (
        <Row gap="0.5rem" style={{ flexWrap: 'wrap' }}>
          {content.split(',')?.map((tag, index) => (
            <Tag
              key={index}
              variant="transparent"
              label={tag.trim()}
              size="small"
            />
          ))}
        </Row>
      )
    case 'text':
    default:
      return <Text.Medium>{content}</Text.Medium>
  }
}

const DataRows = ({ data }: { data: DataSectionItem[] }) => {
  return data?.map((dataItem, index) => (
    <Column
      key={index}
      alignItems="stretch"
      gap="0.5rem"
      style={{
        paddingBlock: '1rem',
        borderBottom: '1px solid var(--gray-300)',
      }}
    >
      <Text.Medium color="#90919E">{dataItem.label}</Text.Medium>
      <DataSectionContent
        content={dataItem.content}
        styling={dataItem.styling}
      />
    </Column>
  ))
}

// TODO: Make it more universal and extract into an independed component
const DataTable = ({ data }: { data: DataSectionItem[] }) => {
  const cellStyle: Style = {
    paddingBlock: '1rem',
  }
  return (
    <table
      style={{
        width: '100%',
        borderCollapse: 'collapse',
      }}
    >
      <tbody>
        {data?.map((dataItem, index) => (
          <tr key={index} style={{ borderBottom: '1px solid var(--gray-300)' }}>
            <td
              style={{ ...cellStyle, width: '18.25rem', verticalAlign: 'top' }}
            >
              {<Text.Medium color="#90919E">{dataItem.label}</Text.Medium>}
            </td>
            <td style={{ ...cellStyle, paddingLeft: '1.5rem' }}>
              <DataSectionContent
                content={dataItem.content}
                styling={dataItem.styling}
              />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

const StepsSection = ({
  listItems,
}: StepSectionType & {
  index?: number
}) => {
  return (
    <Column>
      <ol className="fe ff fg a b z"
      >
        {listItems?.map((step, stepIndex) => (
          <styled.li
            key={stepIndex}
            style={{
              '&::before': {
                counterIncrement: 'step-counter',
                content: 'counter(step-counter) "."',
                color: 'var(--primary-blue)',
                fontFamily: 'FunnelDisplay',
                fontWeight: '700',
                fontStyle: 'normal',
                fontSize: '1.75rem',
                lineHeight: '100%',
                paddingRight: '1rem',
              },
            }}
          >
            <Text.Medium style={{ display: 'inline' }}>
              {step.content}
            </Text.Medium>
            {step.alert && (
              <InfoAlert message={step.alert} style={{ marginTop: '1rem' }} />
            )}
          </styled.li>
        ))}
      </ol>
    </Column>
  )
}

const DocumentsSection = ({ listItems }: DocumentSectionType) => {
  const format = useDateFormat()
  return (
    <Column alignItems="stretch">
      {listItems.map((document, index) => {
        if (!document.file) return null
        const { file } = document
        return (
          <CardWrapper
            key={index}
            data-testid="card"
            style={{ borderRadius: '0.9375rem', cursor: 'pointer' }}
          >
            <a className="fh fi fj"
              href={file.src}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Row style={{ alignItems: 'stretch' }}>
                <Icon
                  variant="blue"
                  icon={
                    file.mimeType === 'application/pdf' ? faFilePdf : faFile
                  }
                />
                <Column gap="0.5rem">
                  <Text.Medium>{document.label ?? file.name}</Text.Medium>
                  <Text.Small>{format(new Date(file.createdAt))}</Text.Small>
                </Column>
                <FontAwesomeIcon
                  style={{
                    width: '1.125rem',
                    height: '1.125rem',
                  }}
                  icon={faArrowUpRightFromSquare}
                />
              </Row>
            </a>
          </CardWrapper>
        )
      })}
    </Column>
  )
}

const LocationSection = ({
  title,
  subtitle,
  lat,
  long,
  marker,
}: LocationSectionType) => {
  return (
    <Column alignItems="stretch">
      <Header title={title} />
      <Text.Medium>{subtitle}</Text.Medium>
      <Suspense>
        <SingleMarkerMap
          lat={lat}
          long={long}
          zoom={15}
          marker={marker}
          height="25rem"
        />
      </Suspense>
    </Column>
  )
}
